import { parseISO } from "date-fns"
import { toISODateString } from "@/utils"
import type { DateKind, IGlobalFilterState } from "@/global_filter/index"

export const GLOBAL_FILTER_URL_KEYS = ["start_date", "end_date", "date_kind"] as const

export type GlobalFilterUrlFields = Pick<IGlobalFilterState, "startDate" | "endDate" | "dateKind">

/** Routes where global date filters sync to/from URL query params. */
export function global_filter_url_sync_enabled(pathname: string): boolean {
  if (pathname === "/deductions" || pathname.startsWith("/deductions/")) {
    return true
  }
  if (pathname === "/transactions" || pathname.startsWith("/transactions/")) {
    return true
  }
  return false
}

export function has_global_filter_url_params(search: string): boolean {
  const params = new URLSearchParams(search)
  return GLOBAL_FILTER_URL_KEYS.some(key => params.has(key))
}

function parse_date_kind(value: string | null): DateKind | undefined {
  if (value === "invoice" || value === "check" || value === "posting") {
    return value
  }
  return undefined
}

function parse_date_param(value: string | null): Date | undefined {
  if (!value) {
    return undefined
  }
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) {
    return undefined
  }
  return parsed
}

/** Parse date filter fields from URL search string. */
export function parse_global_filter_url_params(search: string): Partial<GlobalFilterUrlFields> {
  const params = new URLSearchParams(search)
  const result: Partial<GlobalFilterUrlFields> = {}

  const startDate = parse_date_param(params.get("start_date"))
  if (startDate) {
    result.startDate = startDate
  }

  const endDate = parse_date_param(params.get("end_date"))
  if (endDate) {
    result.endDate = endDate
  }

  const dateKind = parse_date_kind(params.get("date_kind"))
  if (dateKind) {
    result.dateKind = dateKind
  }

  return result
}

export function build_global_filter_search_params(
  filters: GlobalFilterUrlFields
): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.startDate) {
    params.set("start_date", toISODateString(filters.startDate))
  }
  if (filters.endDate) {
    params.set("end_date", toISODateString(filters.endDate))
  }
  if (filters.dateKind) {
    params.set("date_kind", filters.dateKind)
  }
  return params
}

/** Merge global filter params into existing search, preserving unrelated keys. */
export function merge_global_filter_into_search(
  current_search: string,
  filters: GlobalFilterUrlFields
): string {
  const params = new URLSearchParams(current_search)
  for (const key of GLOBAL_FILTER_URL_KEYS) {
    params.delete(key)
  }
  const filter_params = build_global_filter_search_params(filters)
  for (const [key, value] of filter_params.entries()) {
    params.set(key, value)
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("global_filter_url_sync_enabled", () => {
    it("enables deductions routes", () => {
      expect(global_filter_url_sync_enabled("/deductions")).toBe(true)
      expect(global_filter_url_sync_enabled("/deductions/won-pending-payment")).toBe(true)
    })

    it("enables transactions routes", () => {
      expect(global_filter_url_sync_enabled("/transactions")).toBe(true)
      expect(global_filter_url_sync_enabled("/transactions/deductions")).toBe(true)
    })

    it("disables other routes", () => {
      expect(global_filter_url_sync_enabled("/dashboard")).toBe(false)
      expect(global_filter_url_sync_enabled("/accounting/checks")).toBe(false)
    })
  })

  describe("has_global_filter_url_params", () => {
    it("is true when any filter param is present", () => {
      expect(has_global_filter_url_params("?start_date=2025-01-01")).toBe(true)
      expect(has_global_filter_url_params("?date_kind=posting")).toBe(true)
    })

    it("is false when no filter params", () => {
      expect(has_global_filter_url_params("")).toBe(false)
      expect(has_global_filter_url_params("?tab=recap")).toBe(false)
    })
  })

  describe("parse_global_filter_url_params", () => {
    it("parses all fields", () => {
      const parsed = parse_global_filter_url_params(
        "?start_date=2025-01-01&end_date=2025-06-30&date_kind=posting"
      )
      expect(parsed.startDate).toEqual(parseISO("2025-01-01"))
      expect(parsed.endDate).toEqual(parseISO("2025-06-30"))
      expect(parsed.dateKind).toBe("posting")
    })

    it("ignores invalid date_kind", () => {
      const parsed = parse_global_filter_url_params("?date_kind=invalid")
      expect(parsed.dateKind).toBeUndefined()
    })

    it("ignores invalid dates", () => {
      const parsed = parse_global_filter_url_params("?start_date=not-a-date")
      expect(parsed.startDate).toBeUndefined()
    })
  })

  describe("build_global_filter_search_params", () => {
    it("builds yyyy-MM-dd params", () => {
      const params = build_global_filter_search_params({
        startDate: parseISO("2025-01-01"),
        endDate: parseISO("2025-06-30"),
        dateKind: "invoice",
      })
      expect(params.get("start_date")).toBe("2025-01-01")
      expect(params.get("end_date")).toBe("2025-06-30")
      expect(params.get("date_kind")).toBe("invoice")
    })
  })

  describe("merge_global_filter_into_search", () => {
    it("preserves unrelated query params", () => {
      const merged = merge_global_filter_into_search(
        "?foo=bar&start_date=2024-01-01",
        {
          startDate: parseISO("2025-01-01"),
          endDate: parseISO("2025-06-30"),
          dateKind: "check",
        }
      )
      expect(merged).toBe("?foo=bar&start_date=2025-01-01&end_date=2025-06-30&date_kind=check")
    })

    it("returns empty string when only filter params and all replaced", () => {
      const merged = merge_global_filter_into_search("?start_date=2024-01-01", {
        startDate: null,
        endDate: null,
        dateKind: "check",
      })
      expect(merged).toBe("?date_kind=check")
    })
  })
}
